const MAPBOX_OPTIONS = {
    attributionControl: false,
    style: 'https://tiles.locationiq.com/v3/streets/vector.json?key=pk.dc019b9c1bf80a303d63296e785d239c',
    antialias: true,
};

export class Map {
    constructor(element, lat, lng, zoom, exact, radius, color) {
        this.element = element;
        this.lat = parseFloat(lat);
        this.lng = parseFloat(lng);
        this.zoom = parseInt(zoom);
        this.exact = exact === "1";
        this.radius = parseFloat(radius); // Fixed: was storing as this.range
        this.color = color || '#f94600'; // Default color fallback
        this.mapboxgl = maplibregl;

        this.map = this.initialize();
        this.map.on('load', () => {
            this.update();
            this.addMapFeatures(); // Moved circle/marker logic here
        });

        // Store instance directly on the element
        this.element.__mapInstance = this;
    }

    initialize() {
        const options = Object.assign({}, MAPBOX_OPTIONS, {
            container: this.element,
            center: this.getMapBoxCenter(),
            zoom: this.zoom,
        });

        const map = new this.mapboxgl.Map(options);

        // Add controls
        const nav = new this.mapboxgl.NavigationControl({ showCompass: false });
        map.addControl(nav, 'bottom-right');
        map.addControl(new this.mapboxgl.FullscreenControl());
        map.scrollZoom.disable();

        this.addEventHandlers();
        return map;
    }

    addMapFeatures() {
        if (!this.exact) {
            this.addCircle();
        } else {
            this.addMarker();
        }
    }

    addCircle() {
        try {
            // Convert radius from meters to kilometers
            const radiusInKm = this.radius / 1000;

            // Validate radius
            if (!radiusInKm || radiusInKm <= 0) {
                console.warn('Invalid radius for circle:', this.radius);
                return;
            }

            const geoCircle = this.getGeoJSONCircle(this.getMapBoxCenter(), radiusInKm);

            // Remove existing circle if it exists
            if (this.map.getSource('polygon')) {
                this.map.removeLayer('polygon');
                this.map.removeSource('polygon');
            }

            this.map.addSource('polygon', geoCircle);
            this.map.addLayer({
                id: 'polygon',
                type: 'fill',
                source: 'polygon',
                layout: {},
                paint: {
                    'fill-color': this.color,
                    'fill-opacity': 0.2,
                   'fill-outline-color': this.color,
                }
            });

            // Add border for better visibility
            this.map.addLayer({
                id: 'polygon-border',
                type: 'line',
                source: 'polygon',
                layout: {},
                paint: {
                    'line-color': this.color,
                    'line-width': 1
                }
            });

        } catch (error) {
            console.error('Error adding circle to map:', error);
        }
    }

    addMarker() {
        new this.mapboxgl.Marker({
            color: this.color,
        })
            .setLngLat(this.getMapBoxCenter())
            .addTo(this.map);
    }

    getMapBoxCenter() {
        return [this.lng, this.lat];
    }

    getGeoJSONCircle(center, radiusInKm, points = 64) {
        const coords = { latitude: center[1], longitude: center[0] };
        const km = radiusInKm;
        const ret = [];

        // More accurate Earth radius calculations
        const earthRadius = 6371; // Earth's radius in km
        const lat = coords.latitude * Math.PI / 180; // Convert to radians

        for (let i = 0; i <= points; i++) { // Include the last point to close the polygon
            const theta = (i / points) * (2 * Math.PI);

            // More accurate distance calculations
            const deltaLat = (km / earthRadius) * Math.cos(theta);
            const deltaLng = (km / earthRadius) * Math.sin(theta) / Math.cos(lat);

            const newLat = coords.latitude + (deltaLat * 180 / Math.PI);
            const newLng = coords.longitude + (deltaLng * 180 / Math.PI);

            ret.push([newLng, newLat]);
        }

        return {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: [{
                    type: "Feature",
                    geometry: {
                        type: "Polygon",
                        coordinates: [ret]
                    }
                }]
            }
        };
    }

    update() {
        if (this.map) {
            this.map.resize();
            this.map.triggerRepaint();
        }
    }

    // Method to update circle radius dynamically
    updateRadius(newRadius) {
        this.radius = parseFloat(newRadius);
        if (!this.exact && this.map.isStyleLoaded()) {
            this.addCircle();
        }
    }

    // Method to update color dynamically
    updateColor(newColor) {
        this.color = newColor;
        if (!this.exact && this.map.getLayer('polygon')) {
            this.map.setPaintProperty('polygon', 'fill-color', newColor);
            this.map.setPaintProperty('polygon', 'fill-outline-color', newColor);
            if (this.map.getLayer('polygon-border')) {
                this.map.setPaintProperty('polygon-border', 'line-color', newColor);
            }
        }
    }

    addEventHandlers() {
        const touchHandler = () => {
            if (this.map) {
                this.map.touchZoomRotate.enable();
                this.map.dragPan.disable();
            }
            document.removeEventListener('touchstart', touchHandler, { passive: true });
        };

        document.addEventListener('touchstart', touchHandler, { passive: true });
    }

    // Cleanup method
    destroy() {
        if (this.map) {
            this.map.remove();
        }
        if (this.element) {
            delete this.element.__mapInstance;
        }
    }
}